import { useCallback, useEffect, useState } from 'react';

import sellarModule from '../../../api/modules/seller.module';
import useDebounce from '../../../hooks/useDebounce';
import { track } from '../../../analytics/track';
import { showToast } from '../../../utils/toast';
import EditListingModal from '../EditListingModal/EditListingModal';
import { ListingTableView } from './ListingTable.view';

/**
 * "My listings", the container.
 *
 * EVERY REQUEST IS UNCHANGED. `getCardListings` receives the same seven params
 * with the same names and the same `statusFilter !== "All" ? lowercase :
 * undefined` shape, the 500ms search debounce is the same hook, and
 * `bulkDeleteCardListings` still fires the same `listings_bulk_deleted` event
 * with the same two properties. The page-back-on-empty-page rule after a bulk
 * delete is preserved exactly.
 *
 * Selection moved from a local `Set` to an array, because `Table` owns
 * selection and takes `selectedKeys`. The keys are still `list_id`, which is
 * what the delete endpoints take, so nothing about the payload changed.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export default function ListingTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState('All');
  const [conditionFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, statusFilter, conditionFilter, debouncedSearchTerm]);

  const fetchListings = useCallback(async () => {
    setError(null);
    const { success, data, error } = await sellarModule.getCardListings({
      status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
      condition: conditionFilter || undefined,
      search: debouncedSearchTerm || undefined,
      page: currentPage,
      limit: 10,
      sort: sortBy,
      order: sortOrder,
    });

    if (success) {
      setData(data.data);
      setPagination(data?.pagination || { page: 1, limit: 10, total_pages: 0 });
    } else {
      console.error('Error fetching listings:', error);
      setData([]);
      setPagination({ page: 1, limit: 10, total: 0 });
      setError(error || 'Could not load your listings.');
    }
    setLoading(false);
  }, [statusFilter, conditionFilter, debouncedSearchTerm, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const confirmDelete = async () => {
    if (!deleting || !deleting.list_id) return;
    setDeleteBusy(true);
    const { success, error } = await sellarModule.deleteCardListing(deleting.list_id);
    if (success) {
      showToast.success('Listing deleted successfully');
      fetchListings();
    } else {
      showToast.error(error || 'Failed to delete listing');
    }
    setDeleteBusy(false);
    setDeleting(null);
  };

  const confirmBulkDelete = async () => {
    const ids = selectedIds;
    if (!ids.length) return;
    setBulkBusy(true);
    const deletingAllOnPage = ids.length === data.length;
    const { success, error } = await sellarModule.bulkDeleteCardListings(ids);
    if (success) {
      track('listings_bulk_deleted', {
        listings_count: ids.length,
        listing_ids: ids,
      });
      showToast.success(`${ids.length} listing${ids.length > 1 ? 's' : ''} deleted successfully`);
      setSelectedIds([]);
      // If all items on the current page were deleted and we're not on page 1,
      // go back one page so we don't land on an empty page.
      if (deletingAllOnPage && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchListings();
      }
    } else {
      showToast.error(error || 'Failed to delete listings');
    }
    setBulkBusy(false);
    setBulkConfirmOpen(false);
  };

  return (
    <ListingTableView
      rows={data}
      loading={loading}
      error={error}
      onRetry={fetchListings}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      status={statusFilter}
      onStatusChange={setStatusFilter}
      statusOptions={STATUS_OPTIONS}
      sortBy={sortBy}
      onSortByChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      page={pagination.page}
      pageCount={pagination.total_pages || 0}
      pageSize={pagination.limit || 10}
      total={pagination.total ?? data.length}
      onPageChange={setCurrentPage}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      onBulkDelete={() => setBulkConfirmOpen(true)}
      bulkConfirmOpen={bulkConfirmOpen}
      onBulkConfirmClose={() => setBulkConfirmOpen(false)}
      onBulkConfirm={confirmBulkDelete}
      bulkBusy={bulkBusy}
      viewing={viewing}
      onView={setViewing}
      onCloseView={() => setViewing(null)}
      onEdit={(row) => {
        setEditing(row);
        setEditOpen(true);
      }}
      editModal={
        <EditListingModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          listing={editing}
          onUpdate={fetchListings}
        />
      }
      deleting={deleting}
      onDelete={setDeleting}
      onDeleteCancel={() => setDeleting(null)}
      onDeleteConfirm={confirmDelete}
      deleteBusy={deleteBusy}
    />
  );
}
